import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  getOverview,
  getRecentChecks,
  getUptime,
  type CheckRun,
  type OverviewStats,
  type UptimeParams,
  type UptimeStats,
} from "@/lib/api";

export const statsKeys = {
  all: ["stats"] as const,
  overview: () => [...statsKeys.all, "overview"] as const,
  uptime: (params: UptimeParams) =>
    [...statsKeys.all, "uptime", params] as const,
  recentChecks: (urlEntryId: number, limit: number) =>
    [...statsKeys.all, "recent-checks", urlEntryId, limit] as const,
};

export function useStatsOverview(): UseQueryResult<OverviewStats, Error> {
  return useQuery({
    queryKey: statsKeys.overview(),
    queryFn: () => getOverview(),
    refetchInterval: 30_000,
  });
}

export function useUptime(
  params: UptimeParams | null,
): UseQueryResult<UptimeStats, Error> {
  return useQuery({
    queryKey: statsKeys.uptime(params ?? { range: "24h" }),
    queryFn: () => getUptime(params as UptimeParams),
    enabled: params !== null,
  });
}

export function useRecentChecks(
  urlEntryId: number | null,
  limit = 50,
): UseQueryResult<CheckRun[], Error> {
  return useQuery({
    queryKey: statsKeys.recentChecks(urlEntryId ?? 0, limit),
    queryFn: () => getRecentChecks(urlEntryId as number, limit),
    enabled: urlEntryId !== null && urlEntryId !== undefined && urlEntryId > 0,
  });
}
